/* straight from the module, not the `types` barrel: a runtime import of the barrel loads the app */
import { candidatesOf, FleetAccount, FleetLookupRecord } from 'types/fleet-files';

/*
 * The gate's account decisions against a fleet record, kept pure so every case can be tested without
 * rendering the transaction form.
 *
 * One provider customer code may stand for several accounts at a terminal (Flexi-Van reached two at
 * JAX and three at Rincon SAV in production history), and the provider cannot tell them apart. So
 * the check is membership in the candidate set, never equality with one account, and nothing here
 * ever picks one of several for the operator.
 */

/* "A", "A or B", "A, B or C" */
export const formatAccounts = (accounts: FleetAccount[]): string => {
  const names = accounts.map(a => a.name);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

/*
 * The validity message for a selected account the fleet file contradicts, or null when it does not.
 * An empty candidate set is not a contradiction: the code is simply not linked here.
 */
export const fleetAccountConflict = (
  record: FleetLookupRecord,
  unitNumber: string,
  customerId: number
): string | null => {
  const allowed = candidatesOf(record);
  if (!allowed.length || allowed.some(a => a.id === customerId)) return null;
  return `${unitNumber} belongs to a different account in the fleet file. Fleet file allows: ${formatAccounts(
    allowed
  )}`;
};

/*
 * The hint listing the accounts the fleet file allows, shown only when it helps: several
 * candidates, and the account is empty or outside them. With one candidate the account is autofilled
 * instead, and a selection inside the set needs no hint.
 */
export const fleetCandidatesHint = (record: FleetLookupRecord, customerId?: number | null): string | null => {
  const allowed = candidatesOf(record);
  if (allowed.length < 2) return null;
  if (customerId != null && allowed.some(a => a.id === customerId)) return null;
  return `Fleet file allows: ${formatAccounts(allowed)}`;
};
